import { useCallback, useEffect, useRef, useState } from 'react'

import { tMsg } from '@/features/locale/translate'

export type WalletActionButtonLabels = {
  idle: string
  awaitingWallet?: string
  awaitingConfirmation?: string
}

export type WalletActionPhase = 'idle' | 'awaiting-wallet' | 'awaiting-confirmation'

/**
 * Estado de botón para acciones wallet/on-chain: deshabilita al pulsar,
 * distingue espera de firma vs confirmación on-chain, y permite cancelar
 * la espera de firma si el usuario cierra MetaMask sin responder.
 */
export function useWalletActionButton({
  onAction,
  pendingOnChain = false,
  labels,
  disabledWhenIdle = false,
}: {
  onAction: () => void | Promise<void>
  pendingOnChain?: boolean
  labels: WalletActionButtonLabels
  disabledWhenIdle?: boolean
}): {
  label: string
  phase: WalletActionPhase
  isBusy: boolean
  canCancel: boolean
  disabled: boolean
  run: () => void
  cancel: () => void
} {
  const [awaitingWallet, setAwaitingWallet] = useState(false)
  const pendingOnChainRef = useRef(pendingOnChain)
  pendingOnChainRef.current = pendingOnChain

  useEffect(() => {
    if (pendingOnChain) {
      setAwaitingWallet(false)
    }
  }, [pendingOnChain])

  const phase: WalletActionPhase = pendingOnChain
    ? 'awaiting-confirmation'
    : awaitingWallet
      ? 'awaiting-wallet'
      : 'idle'

  const isBusy = phase !== 'idle'
  const canCancel = phase === 'awaiting-wallet'

  const t = tMsg().swapUi
  const label =
    phase === 'awaiting-confirmation'
      ? labels.awaitingConfirmation ?? t.awaitingConfirmation
      : phase === 'awaiting-wallet'
        ? labels.awaitingWallet ?? t.awaitingWalletSignature
        : labels.idle

  const disabled = isBusy || disabledWhenIdle

  const cancel = useCallback(() => {
    setAwaitingWallet(false)
  }, [])

  const run = useCallback(() => {
    if (isBusy || disabledWhenIdle) return
    setAwaitingWallet(true)
    void Promise.resolve(onAction())
      .catch(() => {
        setAwaitingWallet(false)
      })
      .finally(() => {
        queueMicrotask(() => {
          if (!pendingOnChainRef.current) {
            setAwaitingWallet(false)
          }
        })
      })
  }, [disabledWhenIdle, isBusy, onAction])

  return { label, phase, isBusy, canCancel, disabled, run, cancel }
}
